// viewers/ImageViewer.tsx
"use client";

import { useEffect, useMemo, useState } from "react";
import Image from "next/image";
import OcrImageHighlighter from "@/utils/OcrImageHighlighter";
import html2canvas from "html2canvas";

type Props = {
  url: string;
  alt?: string;
  /** Turn on OCR/highlighting mode (defaults to true) */
  enableOCR?: boolean;
  /** If you already have keywords, pass them. Otherwise they'll be read from localStorage. */
  keywords?: string[];
  /** Where to read keywords from in localStorage */
  storageKey?: string; // default: 'enhancedSearchState:v1'
  /** Which property inside that storage object contains the keywords array */
  storagePath?: "searchInput" | "searchInputManageBCR"; // default: 'searchInput'
};

export default function ImageViewer({
  url,
  alt = "Image",
  enableOCR = true,
  keywords,
  storageKey = "enhancedSearchState:v1",
  storagePath = "searchInput",
}: Props) {
  // If keywords aren’t provided, load & live-sync them from localStorage
  const activeKeywords = useLocalStorageKeywords({
    provided: keywords,
    storageKey,
    storagePath,
  });

  if (enableOCR) {
    return (
      <div className="flex justify-center overflow-y-auto relative h-[80vh]">
        <OcrImageHighlighter imageUrl={url} searchKeywords={activeKeywords} />
      </div>
    );
  }

  // Plain image mode (no OCR/highlight)
  return (
    <div className="flex justify-center overflow-y-auto h-[80vh]">
      <Image src={url} alt={alt} width={1200} height={1200} className="rounded" />
    </div>
  );
}

/** Hook: read keywords from localStorage and keep in sync across tabs. */
function useLocalStorageKeywords(opts: {
  provided?: string[];
  storageKey: string;
  storagePath: "searchInput" | "searchInputManageBCR";
}) {
  const { provided, storageKey, storagePath } = opts;
  const [localKeywords, setLocalKeywords] = useState<string[]>([]);

  useEffect(() => {
    if (provided) return; // bypass if caller supplies keywords
    if (typeof window === "undefined") return;

    const read = () => {
      try {
        const raw = localStorage.getItem(storageKey);
        if (!raw) return setLocalKeywords([]);
        const state = JSON.parse(raw);
        const arr = Array.isArray(state?.[storagePath]) ? state[storagePath] : [];
        const cleaned = arr
          .filter((s: unknown): s is string => typeof s === "string")
          .map((s) => s.trim())
          .filter(Boolean);
        setLocalKeywords(cleaned);
      } catch {
        setLocalKeywords([]);
      }
    };

    read();
    const onStorage = (e: StorageEvent) => {
      if (e.key === storageKey) read();
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [provided, storageKey, storagePath]);

  // Prefer provided keywords if present, otherwise local storage ones
  return useMemo(() => provided ?? localKeywords, [provided, localKeywords]);
}

// "use client";
// import Image from "next/image";

// export default function ImageViewer({
//   url, alt = "Image",
// }: { url: string; alt?: string; }) {
//   return (
//     <div className="flex justify-center overflow-y-auto h-[80vh]">
//       <Image src={url} alt={alt} width={1200} height={1200} className="rounded" />
//     </div>
//   );
// }
